import { __ } from '@wordpress/i18n';
import {
  InspectorControls,
  store as blockEditorStore,
} from '@wordpress/block-editor';
import {
  BaseControl,
  Button,
  __experimentalToggleGroupControl as ToggleGroupControl,
  __experimentalToggleGroupControlOption as ToggleGroupControlOption,
} from '@wordpress/components';
import { useSelect, useDispatch } from '@wordpress/data';
import { createBlock } from '@wordpress/blocks';

import PanelBody from '../../components/PanelBody';
import ColorPicker from '../../components/ColorPicker';
import Slider from '../../components/Slider';

export default function Inspector({ attributes, setAttributes, clientId }) {
  const {
    layout,
    contentAlign,
    avatarSize,
    avatarShape,
    avatarRingColor,
    avatarRingWidth,
    hoverEffect,
  } = attributes;

  // brandy/avatar is not listable in the inserter (it is placed by the block
  // template), so an author who deletes the photo has no way to add it back.
  // This restores it rather than flipping Avatar's `inserter` support, which
  // would also change how Avatar Group's custom appender behaves.
  const hasAvatar = useSelect(
    (select) =>
      select(blockEditorStore)
        .getBlocks(clientId)
        .some((block) => block.name === 'brandy/avatar'),
    [clientId]
  );
  const { insertBlock } = useDispatch(blockEditorStore);

  return (
    <InspectorControls>
      <PanelBody title={__('Layout', 'brandy-blocks')} initialOpen>
        <ToggleGroupControl
          label={__('Layout', 'brandy-blocks')}
          value={layout}
          onChange={(value) => setAttributes({ layout: value })}
          isBlock
          __nextHasNoMarginBottom
        >
          <ToggleGroupControlOption value="card" label={__('Card', 'brandy-blocks')} />
          <ToggleGroupControlOption
            value="horizontal"
            label={__('Horizontal', 'brandy-blocks')}
          />
          <ToggleGroupControlOption value="overlay" label={__('Overlay', 'brandy-blocks')} />
        </ToggleGroupControl>

        <ToggleGroupControl
          label={__('Content alignment', 'brandy-blocks')}
          value={contentAlign}
          onChange={(value) => setAttributes({ contentAlign: value })}
          isBlock
          __nextHasNoMarginBottom
        >
          <ToggleGroupControlOption value="left" label={__('Left', 'brandy-blocks')} />
          <ToggleGroupControlOption value="center" label={__('Center', 'brandy-blocks')} />
          <ToggleGroupControlOption value="right" label={__('Right', 'brandy-blocks')} />
        </ToggleGroupControl>

        <ToggleGroupControl
          label={__('Hover effect', 'brandy-blocks')}
          value={hoverEffect}
          onChange={(value) => setAttributes({ hoverEffect: value })}
          isBlock
          __nextHasNoMarginBottom
        >
          <ToggleGroupControlOption value="none" label={__('None', 'brandy-blocks')} />
          <ToggleGroupControlOption value="lift" label={__('Lift', 'brandy-blocks')} />
          <ToggleGroupControlOption value="zoom" label={__('Zoom', 'brandy-blocks')} />
        </ToggleGroupControl>
      </PanelBody>

      <PanelBody title={__('Photo', 'brandy-blocks')} initialOpen>
        {!hasAvatar && (
          <Button
            variant="secondary"
            style={{ marginBottom: '16px' }}
            onClick={() =>
              insertBlock(createBlock('brandy/avatar'), 0, clientId, false)
            }
          >
            {__('Add photo', 'brandy-blocks')}
          </Button>
        )}

        <ToggleGroupControl
          label={__('Shape', 'brandy-blocks')}
          value={avatarShape}
          onChange={(value) => setAttributes({ avatarShape: value })}
          isBlock
          __nextHasNoMarginBottom
        >
          <ToggleGroupControlOption value="circle" label={__('Circle', 'brandy-blocks')} />
          <ToggleGroupControlOption value="rounded" label={__('Rounded', 'brandy-blocks')} />
          <ToggleGroupControlOption value="square" label={__('Square', 'brandy-blocks')} />
        </ToggleGroupControl>

        <Slider
          label={__('Size (px)', 'brandy-blocks')}
          value={avatarSize}
          onChange={(value) => setAttributes({ avatarSize: value })}
          min={48}
          max={320}
        />

        <Slider
          label={__('Ring width (px)', 'brandy-blocks')}
          value={avatarRingWidth}
          onChange={(value) => setAttributes({ avatarRingWidth: value })}
          min={0}
          max={12}
        />

        {avatarRingWidth > 0 && (
          <>
            <BaseControl.VisualLabel>
              {__('Ring color', 'brandy-blocks')}
            </BaseControl.VisualLabel>
            <ColorPicker
              color={avatarRingColor}
              onChange={(value) => setAttributes({ avatarRingColor: value })}
            />
          </>
        )}
      </PanelBody>
    </InspectorControls>
  );
}
